import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'
import SignUpPrompt from '../components/SignUpPrompt'
import {
  Car,
  Compass,
  Flag,
  CalendarDays,
  Star,
  Users,
  Menu,
  X,
  LogIn,
  UserPlus
} from 'lucide-react'

// Navigation items for guests - limited to browsable content
const navSections = [
  {
    label: 'Explore',
    items: [
      { to: '/discover', icon: Compass, label: 'Discover' },
      { to: '/clubs', icon: Flag, label: 'Clubs' },
      { to: '/events', icon: CalendarDays, label: 'Events' },
      { to: '/specialists', icon: Star, label: 'Specialists' },
      { to: '/community', icon: Users, label: 'Discussions' },
    ]
  },
  {
    label: 'Your Garage',
    items: [
      { to: '/garage', icon: Car, label: 'My Garage' },
    ]
  }
]

export default function GuestLayout() {
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false)
  const [promptAction, setPromptAction] = useState('continue')

  const openPrompt = (action = 'continue') => {
    setPromptAction(action)
    setShowSignUpPrompt(true)
  }

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      onClick={() => setMobileMenuOpen(false)}
      className={({ isActive }) =>
        `sidebar-nav-item ${isActive ? 'active' : ''}`
      }
    >
      <Icon size={18} strokeWidth={1.75} />
      <span>{label}</span>
    </NavLink>
  )

  return (
    <div className="flex min-h-screen bg-rdc-cream">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-white rounded-lg shadow-md border border-rdc-stone/30"
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-[260px] sidebar flex flex-col
          transform transition-transform duration-300 ease-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="p-6 flex justify-center">
          <Logo linkTo="/" />
        </div>

        {/* Divider */}
        <div className="mx-6 divider-solid" />

        {/* Guest Banner */}
        <div className="mx-4 mt-4 p-4 bg-gradient-to-br from-rdc-primary/10 to-rdc-gold/10 rounded-xl">
          <p className="text-sm font-medium text-rdc-charcoal mb-2">
            Welcome to Digital Garage
          </p>
          <p className="text-xs text-rdc-taupe mb-3">
            Create an account to track your vehicles and access all features.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="w-full py-2 bg-rdc-primary text-white text-sm font-medium rounded-lg hover:bg-rdc-primary-dark transition-colors"
          >
            Get Started Free
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navSections.map((section, idx) => (
            <div key={section.label} className={idx > 0 ? 'mt-5' : ''}>
              <div className="px-6 mb-2">
                <span className="text-label">{section.label}</span>
              </div>
              {section.items.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </div>
          ))}
        </nav>

        {/* Auth Buttons */}
        <div className="p-4 border-t border-rdc-stone/30">
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/login')}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-rdc-charcoal bg-white border border-rdc-warm-gray rounded-lg hover:bg-rdc-cream transition-colors"
            >
              <LogIn size={16} />
              Sign In
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white bg-rdc-primary rounded-lg hover:bg-rdc-primary-dark transition-colors"
            >
              <UserPlus size={16} />
              Sign Up
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-4 pt-16 sm:p-6 sm:pt-6 lg:p-10 max-w-7xl mx-auto">
          <Outlet context={{ isGuest: true, openPrompt }} />
        </div>

        {/* Footer */}
        <footer className="mt-12 border-t border-rdc-stone/20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6">
            <div className="flex flex-wrap gap-6 text-sm">
              <a href="/" className="text-rdc-taupe hover:text-rdc-charcoal transition-colors">
                Home
              </a>
              <span className="text-rdc-stone">/</span>
              <a href="/privacy" className="text-rdc-taupe hover:text-rdc-charcoal transition-colors">
                Privacy Policy
              </a>
              <span className="text-rdc-stone">/</span>
              <a href="/terms" className="text-rdc-taupe hover:text-rdc-charcoal transition-colors">
                Terms of Service
              </a>
              <span className="text-rdc-stone">/</span>
              <a href="/sms-consent" className="text-rdc-taupe hover:text-rdc-charcoal transition-colors">
                SMS Consent
              </a>
            </div>
            <p className="text-xs text-rdc-taupe mt-4">
              © 2026 Riding & Driving Club. All rights reserved. | michael@ridinganddrivingclub.com
            </p>
          </div>
        </footer>
      </main>

      {/* Sign Up Prompt Modal */}
      <SignUpPrompt
        isOpen={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        action={promptAction}
      />
    </div>
  )
}
